import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AnyAction } from "@reduxjs/toolkit";
import { STATUS } from "../../utils/status";
import SnackbarNotification from "../snackbar/snackbarNotification";
import { RootState } from "../../redux/store/appStore";
import {
  getTagsViaExcel
} from "../../redux/slices/outputJson";
import { JsonTree } from "./jsonTree";
import { JsonValue, Mapping } from "./editor";
import {
  uploadCompletedTask
} from "../../redux/slices/outputJson";
import { setUploadedFiles } from "../../redux/reducers/formattedOutput";
import FileUpload from "./uploadMediaButton";
import type { SelectChangeEvent } from "@mui/material/Select";
import { setMappedArrayKey } from "../../redux/reducers/mappedKeySlice";
interface SheetData {
  name: string;
  data: any[][];
}

interface JsonTreeProps {
  parsedJson: JsonValue;
  keyMappings: Record<string, Mapping>;
  handleMappingChange: (path: string, mapping: Mapping) => void;
  updateParsedJsonValue: any;
  removeIfNull: Record<string, boolean>;
  removeIfBlank: Record<string, boolean>;
  setRemoveIfNull: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setRemoveIfBlank: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const UploadSheetMapper = ({
  parsedJson,
  keyMappings,
  handleMappingChange,
  updateParsedJsonValue,
  removeIfNull,
  removeIfBlank,
  setRemoveIfNull,
  setRemoveIfBlank

}: JsonTreeProps) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedArrayKey, setSelectedArrayKey] = useState("");
  const dispatch = useDispatch();
  const { id } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const createUploadTaskName = useSelector(
    (state: RootState) => state.outputJson.uploadTaskNamesStatus
  );
  const createUploadCompletedTask = useSelector(
    (state: RootState) => state.outputJson.uploadCompletedTaskStatus
  );
  const savedReduxFile = useSelector((state: RootState) => state.output.files);
  const [hasError, setHasError] = useState(false);

  const handleSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    dispatch(setUploadedFiles([file]));

    const reader = new FileReader();
    reader.onload = (event) => {
      const binaryStr = event.target?.result;
      if (typeof binaryStr === "string") {
        setLoading(true);
        const workbook = XLSX.read(binaryStr, { type: "binary" });

        const sheetDataArray = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const sheetData = XLSX.utils.sheet_to_json<any[][]>(worksheet, {
            header: 1,
          });
          return { name: sheetName, data: sheetData };
        });

        setSheets(sheetDataArray);
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  useEffect(() => {
    if (savedReduxFile.length === 0 || !id) {
      return;
    }

    const file = savedReduxFile[0];
    if (!file) {
      handleSnackbar("Invalid file format or missing file", "error");
      return;
    }

      dispatch(
        uploadCompletedTask({ id, selectedFile: file }) as unknown as AnyAction
      );

  }, [savedReduxFile, id]);

  useEffect(() => {
    if (createUploadCompletedTask === STATUS.SUCESS) {
      const file = savedReduxFile[0];
      if (!file) {
        return;
      }
      dispatch(getTagsViaExcel({ id: id || "", file }) as unknown as AnyAction);
    } 
    // else if (
    //   ModeType !== "Type 1" &&
    //   createUploadTaskName === STATUS.SUCESS
    // ) {
    //   dispatch(
    //     getTagsViaPlatform({ project_id: id || "" }) as unknown as AnyAction
    //   );
    // }
  }, [createUploadCompletedTask, createUploadTaskName]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getArrayKeys = (obj: any): string[] => {
    const arrayKeys = new Set<string>();

    const traverse = (value: any) => {
      if (Array.isArray(value)) {
        // Do not track path, just key name from parent object
        // Handled in parent call
      }

      if (typeof value === "object" && value !== null) {
        for (const key in value) {
          const child = value[key];
          if (Array.isArray(child)) {
            arrayKeys.add(key);
            for (const item of child) {
              traverse(item); // Dive into array items
            }
          } else if (typeof child === "object" && child !== null) {
            traverse(child);
          }
        }
      }
    };

    traverse(obj);
    return Array.from(arrayKeys);
  };


  const arrayKeys = parsedJson ? getArrayKeys(parsedJson) : [];

  // const handleArrayKeyChange = (event: SelectChangeEvent<string>) => {
  //     const selectedKey = event.target.value as string;
  //     setSelectedArrayKey(selectedKey);
  //     dispatch(setMappedArrayKey(selectedKey));
  // };

  const handleArrayKeyChange = (event: SelectChangeEvent<string>) => {
    const selectedKey = event.target.value;
    setSelectedArrayKey(selectedKey);
    dispatch(setMappedArrayKey(selectedKey));

    setHasError(!selectedKey || selectedKey === "none");
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flex: 1, gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <FileUpload
            title="Task Export Excel Data:"
            subtext=".csv, .tsv, .xls, .xlsx, .xml are accepted"
            accept=".csv,.tsv,.xls,.xlsx,.xml"
            onFileChange={handleFileUpload}
            buttonLabel="Upload file"
            uploadStatus={createUploadCompletedTask}
            file={savedReduxFile[0]}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: '600' }}>Task Data Mapped With</Typography>
          <FormControl
            sx={{ minWidth: 300 }}
            size="small"
            error={hasError}
            required
          >
            <InputLabel id="map-key-label">Select Option</InputLabel>
            <Select
              labelId="map-key-label"
              value={selectedArrayKey}
              label="Select Key"
              onChange={handleArrayKeyChange}
              onBlur={() => {
                if (!selectedArrayKey || selectedArrayKey === "none") {
                  setHasError(true);
                } else {
                  setHasError(false);
                }
              }}
            >
              {arrayKeys.length > 0 ? (
                arrayKeys.map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="none" disabled>No array keys found</MenuItem>
              )}
            </Select>
            {hasError && (
              <FormHelperText>Please select a valid key</FormHelperText>
            )}
          </FormControl>
        </Box>
      </Box>
      <JsonTree
        data={parsedJson}
        mappings={keyMappings}
        onMappingChange={handleMappingChange}
        onValueChange={updateParsedJsonValue}
        removeIfNull={removeIfNull}
        setRemoveIfNull={setRemoveIfNull}
        removeIfBlank={removeIfBlank}
        setRemoveIfBlank={setRemoveIfBlank}
      />
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default UploadSheetMapper;
